/**
 * `/results` — the learner's results history (P-SCH-D).
 *
 * A server-paginated `<DataTable>` over `learning.learner.attempts.list` —
 * own rows only, newest first — with Quiz / Course, Date, Score %, and Status
 * columns. Clicking a row opens that attempt's `/result/$attemptId` review.
 *
 * Pagination is controlled (the proc takes `limit` / `offset` and returns
 * `total`); a `status` query has no UI yet (the proc supports it, but the
 * full history is the useful default). Mobile-first: the table scrolls
 * horizontally inside its bordered frame at phone width (QB-8), the same as
 * every other `<DataTable>` surface.
 */
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import {
  Award,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  History,
  Search,
  TrendingUp,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  cn,
  DataTable,
  type DataTableColumn,
  EmptyState,
  KpiCard,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';

export const Route = createFileRoute('/results')({
  component: ResultsPage,
});

type AttemptRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['attempts']['list']['items'][number];

const PAGE_SIZE = 25;

function ResultsPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Results" wide>
        <ResultsContent />
      </LearnShell>
    </RequireAuth>
  );
}

/** "82.5%" → at most one decimal, no trailing ".0"; null → "—". */
function formatScorePercent(p: number | null): string {
  if (p == null) return '—';
  return `${Math.round(p * 10) / 10}%`;
}

/**
 * Color-encode the score so pass/fail is glanceable (mirrors the quiz timer
 * pill's band idiom): ≥80 = pass/success, ≥50 = warning, else destructive;
 * null ("—") stays muted. No `--color-success` token exists in the theme, so
 * the pass band falls back to emerald with a dark variant; warning + fail use
 * the shared `--color-warning` / `text-destructive` tokens.
 */
function scoreToneClass(p: number | null): string {
  if (p == null) return 'text-muted-foreground';
  if (p >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (p >= 50) return 'text-[color:var(--color-warning)]';
  return 'text-destructive';
}

/** Wrapped in `new Date(...)` so an offline-persisted (string-revived) row still formats. */
function formatDate(iso: string | null, fallback: string): string {
  const d = new Date(iso ?? fallback);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatusBadge({ status }: { status: AttemptRow['status'] }): React.JSX.Element {
  if (status === 'in_progress') return <Badge variant="secondary">In progress</Badge>;
  if (status === 'submitted') return <Badge variant="warning">Pending grading</Badge>;
  return <Badge variant="outline">Graded</Badge>;
}

/**
 * Status filter chips (Ishraf v1 QA) — the proc already accepts `status`. Typed
 * against the proc INPUT enum (not `AttemptRow['status']`, which Drizzle infers
 * as the wider `string`), so the non-'all' branch narrows to the proc's union.
 */
type StatusFilter = 'all' | 'in_progress' | 'submitted' | 'graded';
const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'submitted', label: 'Pending grading' },
  { value: 'graded', label: 'Graded' },
];

/**
 * Client-side sort over the CURRENTLY LOADED page (v1 — see note in the
 * component). 'date_desc' matches the server's own `startedAt DESC` order so it
 * is a stable no-op default; the other modes re-order just the visible rows.
 */
type SortMode = 'date_desc' | 'date_asc' | 'score_desc';
const SORT_MODES: { value: SortMode; label: string }[] = [
  { value: 'date_desc', label: 'Newest' },
  { value: 'date_asc', label: 'Oldest' },
  { value: 'score_desc', label: 'Score high → low' },
];

/** Best timestamp for ordering a row (submitted, else started). */
function rowTime(row: AttemptRow): number {
  return new Date(row.submittedAt ?? row.startedAt).getTime();
}

function ResultsContent(): React.JSX.Element {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [status, setStatus] = React.useState<StatusFilter>('all');
  // '' = all courses; a non-empty value is a course id passed server-side.
  const [courseId, setCourseId] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<SortMode>('date_desc');

  // The course dropdown is sourced from the learner's ENROLLED catalog courses.
  const catalogQ = trpc.learning.courses.catalog.useQuery();
  const myCourses = (catalogQ.data?.courses ?? []).filter((c) => c.enrolledAlready);

  // `status` + `courseId` are SERVER-side filters (correct across all pages);
  // both follow the same spread-only-when-set pattern, and total comes back
  // from the query so pagination stays accurate.
  const listQ = trpc.learning.learner.attempts.list.useQuery({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    ...(status !== 'all' ? { status } : {}),
    ...(courseId ? { courseId } : {}),
  });

  const serverRows = listQ.data?.items ?? [];
  const total = listQ.data?.total ?? 0;

  // NOTE (v1): text search + sort operate over the CURRENTLY LOADED page only —
  // they re-shape the visible rows, not the full server result set. Full
  // server-side search/sort is a separate backend item. Pagination + `total`
  // remain server-driven and unaffected.
  const rows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? serverRows.filter(
          (r) => r.quizTitle.toLowerCase().includes(q) || r.courseTitle.toLowerCase().includes(q),
        )
      : serverRows;
    const sorted = [...filtered];
    if (sort === 'date_asc') {
      sorted.sort((a, b) => rowTime(a) - rowTime(b));
    } else if (sort === 'score_desc') {
      sorted.sort((a, b) => (b.scorePercent ?? -1) - (a.scorePercent ?? -1));
    } else {
      sorted.sort((a, b) => rowTime(b) - rowTime(a));
    }
    return sorted;
  }, [serverRows, search, sort]);

  // KPI strip — derived from the loaded server page (no new query). Graded rows
  // with a numeric score drive average/best/pass-rate; total reflects the full
  // server count so it stays accurate across pagination.
  const kpis = React.useMemo(() => {
    const scored = serverRows.filter(
      (r): r is AttemptRow & { scorePercent: number } => r.scorePercent != null,
    );
    const graded = serverRows.filter((r) => r.status === 'graded');
    const avg = scored.length
      ? Math.round((scored.reduce((s, r) => s + r.scorePercent, 0) / scored.length) * 10) / 10
      : null;
    const best = scored.length ? Math.max(...scored.map((r) => r.scorePercent)) : null;
    const passed = scored.filter((r) => r.scorePercent >= 80).length;
    const passRate = scored.length ? Math.round((passed / scored.length) * 100) : null;
    return {
      avg,
      best,
      gradedCount: graded.length,
      passed,
      passRate,
      scoredCount: scored.length,
      spark: [...scored].reverse().map((r) => r.scorePercent),
    };
  }, [serverRows]);

  const columns: DataTableColumn<AttemptRow>[] = [
    {
      key: 'quiz',
      header: 'Quiz / Course',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{row.quizTitle}</p>
          <p className="truncate text-xs text-muted-foreground">{row.courseTitle}</p>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      cell: (row) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {formatDate(row.submittedAt, row.startedAt)}
        </span>
      ),
    },
    {
      key: 'score',
      header: 'Score',
      align: 'right',
      cell: (row) => (
        <span className={cn('tabular-nums font-semibold', scoreToneClass(row.scorePercent))}>
          {formatScorePercent(row.scorePercent)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const loading = listQ.isPending;

  return (
    <div className="space-y-6">
      <div className="space-y-1 animate-fade-in">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <span>Results</span>
          <ChevronRight className="size-3" aria-hidden="true" />
          <span>History</span>
        </div>
        <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
          <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
            <History className="size-4" aria-hidden="true" />
          </span>
          Results History
        </h2>
        <p className="text-sm text-muted-foreground">
          Every attempt across your courses — scores, status, and the questions to revisit.
        </p>
      </div>

      <div className="ls-stagger grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          accent="primary"
          icon={TrendingUp}
          label="Average score"
          value={kpis.avg == null ? '—' : `${kpis.avg}%`}
          hint="Graded attempts on this page"
          values={kpis.spark.length > 1 ? kpis.spark : undefined}
          isLoading={loading}
        />
        <KpiCard
          accent="success"
          icon={Award}
          label="Best score"
          value={kpis.best == null ? '—' : `${Math.round(kpis.best * 10) / 10}%`}
          hint="Top result on this page"
          isLoading={loading}
        />
        <KpiCard
          accent="none"
          icon={FileText}
          label="Attempts"
          value={total}
          hint="Across all your courses"
          isLoading={loading}
        />
        <KpiCard
          accent={kpis.passRate != null && kpis.passRate < 50 ? 'warning' : 'success'}
          icon={CheckCircle2}
          label="Passed"
          value={kpis.scoredCount ? `${kpis.passed}/${kpis.scoredCount}` : '—'}
          hint={kpis.passRate == null ? 'No graded scores yet' : `${kpis.passRate}% pass rate`}
          isLoading={loading}
        />
      </div>

      <Card className="card-soft overflow-hidden p-0">
        <div className="space-y-3 border-b border-border/60 p-4">
          <div
            className="flex flex-wrap items-center gap-1.5"
            role="group"
            aria-label="Filter results by status"
          >
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => {
                  setStatus(f.value);
                  setPage(1);
                }}
                data-testid={`results-status-${f.value}`}
                aria-pressed={status === f.value}
                className={cn(
                  'press rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  status === f.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="results-search"
                aria-label="Search results on this page"
                placeholder="Search quiz or course…"
                className={cn(
                  'press w-full rounded-md border bg-background py-1.5 pl-9 pr-3 text-sm',
                  'text-foreground shadow-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  'hover:border-primary/50',
                )}
              />
            </div>

            {myCourses.length > 1 && (
              <div className="relative">
                <select
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    setPage(1);
                  }}
                  data-testid="results-course-filter"
                  aria-label="Filter results by course"
                  className="press min-h-touch appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                >
                  <option value="">All courses</option>
                  {myCourses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
            )}

            <div className="relative">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortMode)}
                data-testid="results-sort"
                aria-label="Sort results on this page"
                className="press min-h-touch appearance-none rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
              >
                {SORT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
            </div>

            <span
              className="text-xs text-muted-foreground sm:ml-auto"
              data-testid="results-count"
              aria-live="polite"
            >
              {rows.length} on this page
            </span>
          </div>
        </div>

        {listQ.isError ? (
          <div className="p-4">
            <EmptyState
              icon={History}
              title="Couldn’t load your results"
              description={listQ.error?.message}
              action={
                <Button variant="outline" onClick={() => void listQ.refetch()}>
                  Try again
                </Button>
              }
            />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.attemptId}
            isLoading={listQ.isPending}
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
            onRowClick={(row) =>
              void navigate({ to: '/result/$attemptId', params: { attemptId: row.attemptId } })
            }
            showIndex
            emptyTitle="No results yet"
            emptyDescription="Take a test or a practice run and your scores will show up here."
            ariaLabel="Your quiz and practice results"
          />
        )}
      </Card>
    </div>
  );
}
